import { DataSource } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { ListFilters, SalesCreditNote, SalesCreditNoteService } from '@galaxy/billing';
import { DateUtilities } from '@vendasta/uikit';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest } from 'rxjs';
import { catchError, finalize, map, startWith, tap } from 'rxjs/operators';
import { getRequestPageSize } from '../shared/datasource-helpers';

/**
 * Data source for the SalesCreditNoteList view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
@Injectable()
export class SalesCreditNoteListDatasource extends DataSource<SalesCreditNote> {
  length$: Observable<number>;
  loading$$: BehaviorSubject<boolean>;
  empty$$: BehaviorSubject<boolean>;

  private filters$: Observable<ListFilters>;
  private filters$$: BehaviorSubject<ListFilters>;
  private paginator: MatPaginator;
  private pageEventWithNoRequest$$: ReplaySubject<null>;
  private salesCreditNotes$$: BehaviorSubject<SalesCreditNote[]>;
  private nextCursor: string;
  private hasMore: boolean;
  private subscriptions: Subscription[] = [];

  constructor(private salesCreditNoteService: SalesCreditNoteService) {
    super();
    this.filters$$ = new BehaviorSubject<ListFilters>(null);
    this.pageEventWithNoRequest$$ = new ReplaySubject<null>(null);
    this.salesCreditNotes$$ = new BehaviorSubject<SalesCreditNote[]>([]);
    this.loading$$ = new BehaviorSubject<boolean>(true);
    this.empty$$ = new BehaviorSubject<boolean>(true);
  }

  initialize(filters$: Observable<ListFilters>, paginator: MatPaginator): void {
    this.filters$ = filters$;
    this.paginator = paginator;
    this.length$ = this.salesCreditNotes$$.pipe(
      map((salesCreditNotes) => {
        if (this.hasMore) {
          // if we have more to get, indicate that we have 1 more page than what's actually loaded
          return salesCreditNotes.length + this.paginator.pageSize;
        }
        return salesCreditNotes.length;
      }),
    );
    this.hasMore = true;

    this.subscriptions.push(
      this.paginator.page
        .pipe(
          startWith(null),
          tap(() => {
            const salesCreditNotes = this.salesCreditNotes$$.getValue();
            const requestPageSize = getRequestPageSize(
              salesCreditNotes.length,
              this.paginator.pageIndex,
              this.paginator.pageSize,
            );
            if (this.hasMore && requestPageSize > 0) {
              this.loadMore(requestPageSize, this.filters$$.getValue());
            } else {
              this.pageEventWithNoRequest$$.next(null);
            }
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      this.filters$
        .pipe(
          tap((f) => {
            this.filters$$.next(f);
            this.resetTable();
            this.loadMore(this.paginator.pageSize, f);
          }),
        )
        .subscribe(),
    );
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<SalesCreditNote[]> {
    // Combine everything that affects the rendered data into one update
    // stream for the data-table to consume.
    const dataMutations = [this.salesCreditNotes$$, this.pageEventWithNoRequest$$];

    return combineLatest([...dataMutations]).pipe(
      map(([si]) => this.getPagedData(si, this.paginator.pageIndex, this.paginator.pageSize)),
    );
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect(): void {
    this.salesCreditNotes$$.complete();
    this.filters$$.complete();
    this.pageEventWithNoRequest$$.complete();
    this.loading$$.complete();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Paginate the data. In here we convert material paginator's index based approach to cursor pagination
   */
  getPagedData(salesCreditNotes: SalesCreditNote[], pageIndex: number, pageSize: number): SalesCreditNote[] {
    // return the whole page, or whatever is left of our creditnote list
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    const endIndexForExisting = Math.min(salesCreditNotes.length, endIndex);
    return salesCreditNotes.slice(startIndex, endIndexForExisting);
  }

  /**
   * load more data. Uses the next cursor to retrieve the next set of data, with the page size specified.
   * combine results with existing list of credit notes and update other metadata for our next call
   */
  private loadMore(pageSize: number, filters: ListFilters): void {
    this.loading$$.next(true);
    this.salesCreditNoteService
      .list(filters, this.nextCursor, pageSize)
      .pipe(
        catchError((err) => {
          throw err;
        }),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe((response) => {
        this.nextCursor = response.nextCursor;
        this.hasMore = response.hasMore;
        response?.results.map((creditNote) => {
          creditNote.dueDate = DateUtilities.stripTimeAndTimeZoneInfo(creditNote.dueDate);
          creditNote.creditNoteDate = DateUtilities.stripTimeAndTimeZoneInfo(creditNote.creditNoteDate);
          return creditNote;
        });
        this.salesCreditNotes$$.next([...this.salesCreditNotes$$.getValue(), ...response.results]);
        if (response.results && response.results.length > 0) {
          this.empty$$.next(false);
        }
      });
  }

  private resetTable(): void {
    this.paginator.pageIndex = 0;
    this.nextCursor = '';
    this.hasMore = true;
    this.salesCreditNotes$$.next([]);
  }
}
